import React, { useState } from 'react'
import VisibilityIcon from '@mui/icons-material/Visibility'
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff'
import {
  customSectionAnchorID,
  PACKET_CUSTOM_SECTION_RICH_TEXT_MAX,
  PACKET_CUSTOM_SECTION_TITLE_MAX,
  PacketCustomSection,
} from '@marketing-milk/interfaces'
import { ContentEditableInput } from 'components/mentions/ContentEditableInput'
import { useDeleteCustomSection, useMoveCustomSection } from 'hooks/api/useSprintPackets'
import { radius, SurfacePalette, type } from '../../tokens'
import { CommentIndicator } from '../../components/CommentIndicator'
import { HiddenFromClientChip } from '../../components/HiddenFromClientChip'
import { NarrativeBlock } from '../../components/NarrativeBlock'
import { useUnresolvedCountLookup } from '../UnresolvedCommentsContext'
import { usePacketSections } from '../PacketSectionsContext'
import { customSectionKey } from '../sections'
import { useCustomSectionSave } from './useCustomSectionSave'

const noop = () => undefined
const NO_TAGGED_USERS: never[] = []

interface Props {
  palette: SurfacePalette
  packetID: number
  section: PacketCustomSection
  canMoveUp: boolean
  canMoveDown: boolean
}

/**
 * The edit-mode card for an AE-authored slide: an editable title and a body,
 * saved together when focus leaves the card (see {@link useCustomSectionSave}).
 *
 * Repositioning is up/down arrows against the *visible* deck, not drag — no
 * drag library, and one press moves one place past whatever is actually next,
 * built-in or custom. Drag-and-drop is deliberately deferred.
 *
 * The "Carried forward" badge is edit-mode only. It must never appear in
 * Present: the client may be in the room, and "we haven't looked at this yet"
 * is not a thing to project.
 */
export const CustomSection = ({ palette, packetID, section, canMoveUp, canMoveDown }: Props) => {
  const sectionKey = customSectionKey(section.id)
  const { numberFor, hide } = usePacketSections()
  const number = numberFor(sectionKey)
  const isHidden = !!hide?.hidden.has(sectionKey)
  const countFor = useUnresolvedCountLookup()
  const { title, setTitle, text, setText, indicator, wrapperRef } = useCustomSectionSave(
    packetID,
    section
  )
  const move = useMoveCustomSection(packetID)
  const remove = useDeleteCustomSection(packetID)
  const [confirmingDelete, setConfirmingDelete] = useState(false)

  const anchorID = customSectionAnchorID(packetID, section.id)

  return (
    <section
      id={sectionKey}
      data-section-key={sectionKey}
      data-comment-anchor-type="section"
      data-comment-anchor-id={anchorID}
      data-comment-label={title}
      style={{
        padding: 28,
        borderRadius: radius.lg,
        border: `1px solid ${palette.hairline}`,
        // Same recessed treatment `SectionCard` gives a hidden slide — the
        // surface drops to the page background rather than dimming with
        // opacity, which would make the unhide control harder to see and click.
        background: isHidden ? palette.bg : palette.surface,
        scrollMarginTop: 16,
        fontFamily: type.family.sans,
        color: palette.text,
      }}
    >
      <div ref={wrapperRef}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 16,
            paddingBottom: 8,
            marginBottom: 14,
            borderBottom: `1px solid ${palette.hairline}`,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0, flex: 1 }}>
            {/* A hidden slide isn't in the numbered deck, so `numberFor` would
                fall through to the '00' orphan fallback. */}
            {!isHidden && (
              <span
                style={{
                  fontFamily: type.family.mono,
                  fontSize: type.size.eyebrow,
                  fontWeight: type.weight.medium,
                  color: palette.accent,
                  letterSpacing: '0.12em',
                }}
              >
                {number}
              </span>
            )}
            <input
              value={title}
              onChange={e => setTitle(e.target.value)}
              maxLength={PACKET_CUSTOM_SECTION_TITLE_MAX}
              aria-label="Slide title"
              placeholder="Slide title"
              style={{
                flex: 1,
                minWidth: 0,
                padding: '2px 6px',
                margin: '-2px -6px',
                border: '1px solid transparent',
                borderRadius: radius.sm,
                background: 'transparent',
                color: palette.text,
                fontFamily: type.family.sans,
                fontSize: type.size.h2,
                fontWeight: type.weight.semibold,
                lineHeight: 1.15,
                outline: 'none',
              }}
              onFocus={e => (e.currentTarget.style.borderColor = palette.hairline)}
              onBlur={e => (e.currentTarget.style.borderColor = 'transparent')}
            />
            {section.needsReview && (
              <span
                title="Carried forward from the previous packet and not reviewed yet. Editing this slide clears it."
                style={{
                  flexShrink: 0,
                  padding: '2px 8px',
                  borderRadius: 999,
                  border: `1px solid ${palette.accent}`,
                  color: palette.accent,
                  fontFamily: type.family.mono,
                  fontSize: 10,
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                }}
              >
                Carried forward
              </span>
            )}
            {isHidden && (
              <HiddenFromClientChip
                palette={palette}
                title="This slide is skipped in Present. It stays hidden on next sprint's packet until you show it again."
              />
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
            <button
              type="button"
              aria-label={`Move ${title} up`}
              disabled={!canMoveUp || move.isLoading}
              onClick={() => move.mutate({ id: section.id, direction: 'up' })}
              style={iconBtn(palette, !canMoveUp || move.isLoading)}
            >
              ↑
            </button>
            <button
              type="button"
              aria-label={`Move ${title} down`}
              disabled={!canMoveDown || move.isLoading}
              onClick={() => move.mutate({ id: section.id, direction: 'down' })}
              style={iconBtn(palette, !canMoveDown || move.isLoading)}
            >
              ↓
            </button>
            {hide && (
              <button
                type="button"
                aria-label={`${isHidden ? 'Show' : 'Hide'} ${title}`}
                aria-pressed={isHidden}
                title={
                  isHidden ? 'Show this slide to the client' : 'Hide this slide from the client'
                }
                disabled={hide.pending}
                onClick={() => hide.toggle(sectionKey, !isHidden)}
                style={{
                  ...iconBtn(palette, hide.pending),
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {isHidden ? (
                  <VisibilityOffIcon style={{ fontSize: 15 }} />
                ) : (
                  <VisibilityIcon style={{ fontSize: 15 }} />
                )}
              </button>
            )}
            <button
              type="button"
              aria-label={`Delete ${title}`}
              disabled={remove.isLoading}
              onClick={() => setConfirmingDelete(true)}
              style={iconBtn(palette, remove.isLoading)}
            >
              ×
            </button>
            <CommentIndicator
              palette={palette}
              label={title}
              unresolvedCount={countFor('section', anchorID)}
            />
          </div>
        </div>

        <NarrativeBlock palette={palette}>
          <ContentEditableInput
            value={text}
            onChange={next => setText(next.slice(0, PACKET_CUSTOM_SECTION_RICH_TEXT_MAX))}
            placeholder={`Write the ${title} update…`}
            // Mentions stay off here for the same reason as the narrative
            // editors: prose has no "send", so an `@Sarah` would silently fail
            // to notify Sarah. Comments are the authorized ping path.
            onCursorPositionChange={noop}
            taggedUsers={NO_TAGGED_USERS}
          />
          <div
            style={{
              marginTop: 6,
              fontFamily: type.family.mono,
              fontSize: 11,
              color: palette.textFaint,
              minHeight: 14,
            }}
          >
            {indicator}
          </div>
        </NarrativeBlock>
      </div>

      {confirmingDelete && (
        <div
          role="alertdialog"
          aria-label={`Delete ${title}?`}
          style={{
            marginTop: 14,
            padding: 14,
            borderRadius: radius.sm,
            border: `1px solid ${palette.hairline}`,
            background: palette.bg,
          }}
        >
          <p style={{ margin: '0 0 10px', fontSize: type.size.body, color: palette.textMuted }}>
            Delete "{title}"? Its text and any comments on it go with it.
          </p>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              type="button"
              onClick={() => setConfirmingDelete(false)}
              style={ghostBtn(palette)}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => remove.mutate({ id: section.id })}
              style={{ ...ghostBtn(palette), borderColor: palette.accent, color: palette.accent }}
            >
              Delete slide
            </button>
          </div>
        </div>
      )}
    </section>
  )
}

const iconBtn = (palette: SurfacePalette, disabled: boolean): React.CSSProperties => ({
  width: 26,
  height: 26,
  lineHeight: 1,
  padding: 0,
  borderRadius: radius.sm,
  border: `1px solid ${palette.hairline}`,
  background: 'transparent',
  color: disabled ? palette.textFaint : palette.textMuted,
  fontSize: 14,
  cursor: disabled ? 'default' : 'pointer',
  opacity: disabled ? 0.5 : 1,
})

const ghostBtn = (palette: SurfacePalette): React.CSSProperties => ({
  border: `1px solid ${palette.hairline}`,
  background: 'transparent',
  color: palette.textMuted,
  borderRadius: radius.sm,
  padding: '6px 14px',
  fontFamily: type.family.sans,
  fontSize: type.size.body,
  cursor: 'pointer',
})
